import { HugeiconsIcon } from '@hugeicons/react'
import { Tick01Icon } from '@hugeicons/core-free-icons'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useConvexCreateBulkAssessments } from '../services/assessments.convex'
import type { AnyFieldApi } from '@tanstack/react-form'
import type { ConvexDirectReport } from '@/features/direct-reports/services/direct-reports.convex'
import type { AnyReactFormApi } from '@/types/form'
import { useMyDirectReports } from '@/features/direct-reports/services/direct-reports.convex'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useFormDialog } from '@/contexts/form-dialog-context'
import { useMyConvexOrganizationId } from '@/features/auth'
import { usePeriodReadiness } from '@/features/organization/services/period-config.convex'

interface BulkAssessDirectReportsButtonProps {
  children?: React.ReactNode
  onSuccess?: (data: BulkAssessResponse) => void
}

interface BulkAssessFormData {
  selectAll: boolean
  selectedIds: Array<string>
}

interface BulkAssessResponse {
  successCount: number
  errorCount: number
}

interface VirtualizedDirectReportsListProps {
  directReports: Array<ConvexDirectReport>
  selectedIds: Array<string>
  onToggle: (userId: string) => void
  canLoadMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
}

const VirtualizedDirectReportsList = ({
  directReports,
  selectedIds,
  onToggle,
  canLoadMore,
  isLoadingMore,
  onLoadMore,
}: VirtualizedDirectReportsListProps) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: canLoadMore ? directReports.length + 1 : directReports.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 65,
    overscan: 5,
  })

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse()

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!lastItem) return

    if (
      lastItem.index >= directReports.length - 1 &&
      canLoadMore &&
      !isLoadingMore
    ) {
      onLoadMore()
    }
  }, [
    canLoadMore,
    onLoadMore,
    directReports.length,
    isLoadingMore,
    rowVirtualizer.getVirtualItems(),
  ])

  if (directReports.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground border rounded-lg">
        No direct reports found
      </div>
    )
  }

  return (
    <div
      ref={parentRef}
      className="h-[250px] overflow-y-auto border rounded-lg bg-background [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/30"
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const isLoaderRow = virtualRow.index > directReports.length - 1
          const directReport = directReports[virtualRow.index]

          if (isLoaderRow) {
            return canLoadMore ? (
              <div
                key="loader"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className="flex items-center justify-center text-sm text-muted-foreground"
              >
                Loading more...
              </div>
            ) : null
          }

          const isSelected = selectedIds.includes(directReport.userId)

          return (
            <div
              key={directReport._id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="flex items-center gap-3 px-4 hover:bg-muted/50 transition-colors bg-background"
            >
              <Checkbox
                id={`sub-${directReport.userId}`}
                checked={isSelected}
                onCheckedChange={() => onToggle(directReport.userId)}
              />
              <Label
                htmlFor={`sub-${directReport.userId}`}
                className="flex-1 flex items-center gap-3 cursor-pointer py-2.5"
              >
                <AvatarWithInitials
                  name={directReport.name ?? directReport.email ?? '?'}
                  src={directReport.image ?? undefined}
                  className="h-10 w-10"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {directReport.name}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {directReport.email}
                  </div>
                </div>
                {directReport.position && (
                  <Badge variant="outline" className="shrink-0">
                    {directReport.position.level}
                  </Badge>
                )}
              </Label>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const BulkAssessDialogContent = ({ form }: { form: AnyReactFormApi }) => {
  const { results, status, loadMore } = useMyDirectReports()
  const canLoadMore = status === 'CanLoadMore'
  const isLoadingMore = status === 'LoadingMore'

  if (status === 'LoadingFirstPage') {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Loading direct reports...
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground border rounded-lg">
        No direct reports found
      </div>
    )
  }

  return (
    <>
      {/* Select All Checkbox */}
      <form.Field name="selectAll">
        {(field: AnyFieldApi) => (
          <div className="flex items-center space-x-2 pb-3">
            <Checkbox
              id="select-all"
              checked={field.state.value}
              onCheckedChange={(checked) => {
                field.handleChange(checked)
                if (checked) {
                  form.setFieldValue(
                    'selectedIds',
                    results.map((dr) => dr.userId),
                  )
                } else {
                  form.setFieldValue('selectedIds', [])
                }
              }}
            />
            <Label htmlFor="select-all" className="font-medium cursor-pointer">
              Select All ({results.length} direct reports)
            </Label>
          </div>
        )}
      </form.Field>

      {/* Direct reports list */}
      <form.Field name="selectedIds">
        {(field: AnyFieldApi) => {
          const selectedIds: Array<string> = field.state.value || []

          const handleToggle = (userId: string) => {
            const newSelectedIds = selectedIds.includes(userId)
              ? selectedIds.filter((id) => id !== userId)
              : [...selectedIds, userId]

            field.handleChange(newSelectedIds)
            form.setFieldValue(
              'selectAll',
              newSelectedIds.length === results.length,
            )
          }

          return (
            <VirtualizedDirectReportsList
              directReports={results}
              selectedIds={selectedIds}
              onToggle={handleToggle}
              canLoadMore={canLoadMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={() => loadMore(5)}
            />
          )
        }}
      </form.Field>

      {/* Selected count */}
      <form.Subscribe
        selector={(state: { values: BulkAssessFormData }) =>
          state.values.selectedIds
        }
      >
        {(selectedIds: Array<string>) => (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <HugeiconsIcon icon={Tick01Icon} className="h-4 w-4" />
            {selectedIds.length || 0} direct report
            {(selectedIds.length || 0) !== 1 ? 's' : ''} selected
          </div>
        )}
      </form.Subscribe>
    </>
  )
}

export const BulkAssessDirectReportsButton = ({
  children,
  onSuccess,
}: BulkAssessDirectReportsButtonProps) => {
  const { showFormDialog } = useFormDialog()
  const createBulkAssessmentsMutation = useConvexCreateBulkAssessments()
  const convexOrgId = useMyConvexOrganizationId()
  const readiness = usePeriodReadiness(convexOrgId)

  const disabled =
    readiness.status !== 'ready' && readiness.status !== 'loading'
  const disabledReason =
    readiness.status === 'not-configured'
      ? 'Configure assessment periods first in organization settings.'
      : readiness.status === 'awaiting-start'
        ? `Assessments unlock on ${new Date(readiness.startDate).toLocaleDateString()}.`
        : readiness.status === 'no-current-period'
          ? 'No active assessment period — open organization settings to generate one.'
          : null

  const handleClick = () => {
    showFormDialog<BulkAssessFormData, BulkAssessResponse>({
      title: 'Assess all direct reports',
      description:
        'Select the direct reports you want to start a new assessment for.',
      submitText: 'Create Assessments',
      showSuccessState: false,
      formOptions: {
        defaultValues: {
          selectAll: false,
          selectedIds: [],
        },
      },
      onSubmit: async (
        formData: BulkAssessFormData,
      ): Promise<BulkAssessResponse> => {
        const result = await createBulkAssessmentsMutation.mutateAsync({
          employeeUserIds: formData.selectedIds,
          organizationId: convexOrgId ?? '',
        })

        return {
          successCount: result.successCount,
          errorCount: result.errorCount,
        }
      },
      onSuccess: (result) => {
        if (result.successCount > 0) {
          toast.success(
            `Successfully created ${result.successCount} assessment${result.successCount > 1 ? 's' : ''}${result.errorCount > 0 ? `. ${result.errorCount} failed.` : ''}`,
          )
        } else {
          toast.error('Failed to create any assessments')
        }
        onSuccess?.(result)
      },
      children: (form) => <BulkAssessDialogContent form={form} />,
    })
  }

  const button = (
    <Button onClick={handleClick} disabled={disabled}>
      {children || 'Assess all direct reports'}
    </Button>
  )

  if (disabled && disabledReason) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Wrapper so the tooltip still triggers over a disabled button. */}
            <span className="inline-block">{button}</span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return button
}
